import { useState } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  InputGroup,
  InputGroupText,
  Button,
  Row,
  Col,
  Alert,
} from 'reactstrap';
import { isApiError } from '../../api/errors';
import { useCreateProduct, useUpdateProduct } from './useProducts';
import { KNOWN_DRIVERS, KNOWN_TAX_CATEGORIES } from './fixture';

const fmtEur = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });

export function ProductFormModal({ isOpen, product, onClose }) {
  const isEdit = !!product;
  const [form, setForm] = useState(() => initialForm(product));
  const [jsonError, setJsonError] = useState(null);
  const [error, setError] = useState(null);
  const create = useCreateProduct();
  const update = useUpdateProduct();
  const busy = create.isPending || update.isPending;

  const set = (key) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [key]: val }));
  };

  const finish = () => onClose();

  const parseDriverConfig = () => {
    if (!form.driverConfig.trim()) return {};
    try {
      const parsed = JSON.parse(form.driverConfig);
      setJsonError(null);
      return parsed;
    } catch (e) {
      setJsonError(e.message);
      return null;
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    const driverConfig = parseDriverConfig();
    if (driverConfig === null) {
      setError('driverConfig ist kein gültiges JSON.');
      return;
    }
    // Metered-Billing: meteringConfig nur bei Modus != none, JSON-validiert.
    let meteringConfig = null;
    if (form.meteringMode !== 'none' && form.meteringConfig.trim()) {
      try { meteringConfig = JSON.parse(form.meteringConfig); }
      catch (err2) { setError(`meteringConfig ist kein gültiges JSON: ${err2.message}`); return; }
    }
    // CORE-Kontrakt (ProductSchema, .strict()): recurring ist ein Enum
    // ('one_time'|'month'|'year'), sku ist auch beim Update Pflicht, und
    // Extra-Felder (billingInterval/minimumTermMonths/noticePeriodDays)
    // werden abgelehnt. Das Formular war gegen die Fixture gebaut → 400.
    const recurring = (form.recurring === true)
      ? (['month', 'year'].includes(form.billingInterval) ? form.billingInterval : 'month')
      : (['month', 'year'].includes(form.recurring) ? form.recurring : 'one_time');
    const payload = {
      sku: form.sku.trim(),
      name: form.name.trim(),
      description: form.description.trim() || null,
      driver: form.driver || null,
      driverConfig,
      unitAmount: Number(form.unitAmount) || 0,
      currency: form.currency,
      vatRate: Number(form.vatRate) || 0,
      taxCategory: form.taxCategory,
      recurring,
      trialPeriodDays: Number(form.trialPeriodDays) || 0,
      isActive: form.isActive,
      meteringMode: form.meteringMode,
      meteringConfig,
    };

    try {
      if (isEdit) {
        await update.mutateAsync({ id: product.id, patch: payload });
      } else {
        await create.mutateAsync(payload);
      }
      finish();
    } catch (err) {
      if (isApiError(err) && (err.code === 'ER_DUP_ENTRY' || err.status === 409)) {
        setError(`SKU "${form.sku}" existiert bereits in dieser Brand.`);
      } else {
        setError(isApiError(err) ? err.message : err?.message ?? 'Speichern fehlgeschlagen.');
      }
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={busy ? undefined : finish} size="lg" backdrop="static">
      <ModalHeader toggle={busy ? undefined : finish}>
        {isEdit ? `Produkt #${product.id} bearbeiten` : 'Neues Produkt'}
      </ModalHeader>
      <Form onSubmit={onSubmit}>
        <ModalBody>
          {/* Block: Identifikation */}
          <h6 className="text-uppercase small text-muted">Identifikation</h6>
          <Row>
            <Col md="6">
              <FormGroup>
                <Label>SKU *</Label>
                <Input
                  value={form.sku}
                  onChange={set('sku')}
                  placeholder="z. B. pro-monthly"
                  required
                  disabled={isEdit}
                  maxLength={64}
                />
                {isEdit && (
                  <small className="text-muted">SKU ist nach Anlage read-only (Snapshot in Orders).</small>
                )}
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label>Name *</Label>
                <Input value={form.name} onChange={set('name')} required maxLength={100} />
              </FormGroup>
            </Col>
          </Row>
          <FormGroup>
            <Label>Beschreibung</Label>
            <Input type="textarea" rows={2} value={form.description} onChange={set('description')} maxLength={500} />
          </FormGroup>

          <hr />

          {/* Block: Preis */}
          <h6 className="text-uppercase small text-muted">Preis</h6>
          <Row>
            <Col md="4">
              <FormGroup>
                <Label>unitAmount (Cent) *</Label>
                <InputGroup>
                  <Input
                    type="number"
                    min={0}
                    value={form.unitAmount}
                    onChange={set('unitAmount')}
                    required
                  />
                  <InputGroupText>¢</InputGroupText>
                </InputGroup>
                <small className="text-muted">
                  = {fmtEur.format((Number(form.unitAmount) || 0) / 100)}
                </small>
              </FormGroup>
            </Col>
            <Col md="2">
              <FormGroup>
                <Label>Currency</Label>
                <Input type="select" value={form.currency} onChange={set('currency')}>
                  <option value="EUR">EUR</option>
                  <option value="USD">USD</option>
                  <option value="CHF">CHF</option>
                </Input>
              </FormGroup>
            </Col>
            <Col md="3">
              <FormGroup>
                <Label>VAT-Rate</Label>
                <InputGroup>
                  <Input type="number" min={0} max={50} step={0.1} value={form.vatRate} onChange={set('vatRate')} />
                  <InputGroupText>%</InputGroupText>
                </InputGroup>
              </FormGroup>
            </Col>
            <Col md="3">
              <FormGroup>
                <Label>Tax-Category</Label>
                <Input type="select" value={form.taxCategory} onChange={set('taxCategory')}>
                  {KNOWN_TAX_CATEGORIES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
          </Row>

          <hr />

          {/* Block: Abrechnung */}
          <h6 className="text-uppercase small text-muted">Abrechnung</h6>
          <Row>
            <Col md="4">
              <FormGroup>
                <Label className="d-block">Recurring</Label>
                <div className="form-check form-switch">
                  <Input
                    type="switch"
                    role="switch"
                    id="recurringSwitch"
                    checked={form.recurring}
                    onChange={set('recurring')}
                  />
                  <Label check for="recurringSwitch">{form.recurring ? 'Abo' : 'Einmal-Kauf'}</Label>
                </div>
              </FormGroup>
            </Col>
            {form.recurring && (
              <Col md="3">
                <FormGroup>
                  <Label>Billing-Interval</Label>
                  <Input type="select" value={form.billingInterval} onChange={set('billingInterval')}>
                    <option value="day">day</option>
                    <option value="week">week</option>
                    <option value="month">month</option>
                    <option value="year">year</option>
                  </Input>
                </FormGroup>
              </Col>
            )}
            <Col md={form.recurring ? '2' : '4'}>
              <FormGroup>
                <Label>Trial-Tage</Label>
                <Input type="number" min={0} value={form.trialPeriodDays} onChange={set('trialPeriodDays')} />
              </FormGroup>
            </Col>
            <Col md={form.recurring ? '2' : '4'}>
              <FormGroup>
                <Label>Mindestlfz. (Mon.)</Label>
                <Input type="number" min={0} value={form.minimumTermMonths} onChange={set('minimumTermMonths')} />
              </FormGroup>
            </Col>
            <Col md={form.recurring ? '12' : '4'}>
              <FormGroup>
                <Label>Kündigungsfrist (Tage)</Label>
                <Input type="number" min={0} value={form.noticePeriodDays} onChange={set('noticePeriodDays')} />
              </FormGroup>
            </Col>
          </Row>

          <hr />

          {/* Block: Metered-Billing */}
          <h6 className="text-uppercase small text-muted">Metered-Billing (nutzungsbasiert)</h6>
          <Row>
            <Col md="4">
              <FormGroup>
                <Label>Modus</Label>
                <Input type="select" value={form.meteringMode} onChange={set('meteringMode')}>
                  <option value="none">none — nur Festpreis</option>
                  <option value="overage">overage — Kontingent + Übernutzung</option>
                  <option value="payg">payg — voll metered</option>
                </Input>
              </FormGroup>
            </Col>
            <Col md="8">
              {form.meteringMode !== 'none' && (
                <FormGroup>
                  <Label>meteringConfig (JSON)</Label>
                  <Input
                    type="textarea"
                    rows={5}
                    value={form.meteringConfig}
                    onChange={set('meteringConfig')}
                    style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}
                    placeholder={'{\n  "quotas": { "storage_gb_month": 100, "vcpu_month": 2 },\n  "rates":  { "storage_gb_month": 4 }\n}'}
                  />
                  <small className="text-muted">
                    Einheiten: vcpu_month, ram_gb_month, storage_gb_month, bulk_gb_month,
                    traffic_gb, requests, mailboxes, seats. <code>rates</code> in Cent (netto) —
                    fehlt eine Rate, greift der globale POD_PRICE. <code>quotas</code> nur bei overage.
                  </small>
                </FormGroup>
              )}
            </Col>
          </Row>

          <hr />

          {/* Block: Provisioning */}
          <h6 className="text-uppercase small text-muted">Provisioning</h6>
          <Row>
            <Col md="4">
              <FormGroup>
                <Label>Driver</Label>
                <Input type="select" value={form.driver ?? ''} onChange={set('driver')}>
                  {KNOWN_DRIVERS.map((d) => (
                    <option key={d || 'none'} value={d}>
                      {d || '(kein Driver — valiKey-Only)'}
                    </option>
                  ))}
                </Input>
                <small className="text-muted">
                  Driver-Wechsel auf aktivem Produkt: alte Services behalten den alten Driver.
                </small>
              </FormGroup>
            </Col>
            <Col md="8">
              <FormGroup>
                <Label>driverConfig (JSON)</Label>
                <Input
                  type="textarea"
                  rows={4}
                  value={form.driverConfig}
                  onChange={set('driverConfig')}
                  style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}
                />
                {jsonError && (
                  <small className="text-danger">JSON-Fehler: {jsonError}</small>
                )}
              </FormGroup>
            </Col>
          </Row>

          {error && <Alert color="danger">{error}</Alert>}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={finish} disabled={busy}>Abbrechen</Button>
          <Button color="primary" type="submit" disabled={busy}>
            {busy ? 'Speichern…' : (isEdit ? 'Änderungen speichern' : 'Anlegen')}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
}

function initialForm(product) {
  if (!product) {
    return {
      sku: '',
      name: '',
      description: '',
      driver: 'enhance',
      driverConfig: '{}',
      unitAmount: '999',
      currency: 'EUR',
      vatRate: '19',
      taxCategory: 'standard',
      recurring: true,
      billingInterval: 'month',
      trialPeriodDays: '0',
      minimumTermMonths: '0',
      noticePeriodDays: '30',
      isActive: true,
      meteringMode: 'none',
      meteringConfig: '',
    };
  }
  return {
    sku: product.sku ?? '',
    name: product.name ?? '',
    description: product.description ?? '',
    driver: product.driver ?? 'enhance',
    driverConfig: product.driverConfig ? JSON.stringify(product.driverConfig, null, 2) : '{}',
    unitAmount: String(product.unitAmount ?? 0),
    currency: product.currency ?? 'EUR',
    vatRate: String(product.vatRate ?? 0),
    taxCategory: product.taxCategory ?? 'standard',
    recurring: product.recurring ?? true,
    billingInterval: product.billingInterval ?? 'month',
    trialPeriodDays: String(product.trialPeriodDays ?? 0),
    minimumTermMonths: String(product.minimumTermMonths ?? 0),
    noticePeriodDays: String(product.noticePeriodDays ?? 30),
    isActive: product.isActive ?? true,
    meteringMode: product.meteringMode ?? 'none',
    meteringConfig: product.meteringConfig ? JSON.stringify(product.meteringConfig, null, 2) : '',
  };
}
